import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useScrollTrigger,
  Tooltip,
} from "@material-ui/core";
import {
  Edit as EditIcon,
  LibraryMusic as LibraryMusicIcon,
  PersonAdd as PersonAddIcon,
} from "@material-ui/icons";
import SpotifyIcon from "components/SvgIcons/Spotify";
import { makeStyles, useTheme } from "@material-ui/styles";
import InviteDialog from "components/InviteDialog";
import { teamLabel } from "utils/team";
import filters from "utils/search";

// A stable, deterministic color per member (same idea WhatsApp/Slack/etc.
// use for initials avatars) - hashed from the id so it doesn't shift if
// someone's name changes, and doesn't need any state or a network image.
const AVATAR_HUES = [4, 28, 84, 152, 200, 231, 271, 320];
const avatarColor = (id) => {
  const hue = AVATAR_HUES[id % AVATAR_HUES.length];
  return `hsl(${hue}, 45%, 42%)`;
};

const initials = (user) => {
  const letters = `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`;
  return (letters || user.email?.[0] || "?").toUpperCase();
};

const useStyles = makeStyles((theme) => {
  return {
    spotifyInfoBox: {
      backgroundColor: "#1DB954",
      maxWidth: "440px",
      borderRadius: "4px",
      gap: theme.spacing(1),
    },
    spotifyInfoIcon: {
      marginRight: theme.spacing(1),
    },
    // The member dialog has up to three actions (Close, Remove from team,
    // Make team leader). Side by side they wrap onto several lines each on a
    // phone, so there they stack full-width instead - reversed, so the main
    // action sits on top and Close at the bottom.
    dialogActions: {
      [theme.breakpoints.down("xs")]: {
        flexDirection: "column-reverse",
        alignItems: "stretch",
        padding: theme.spacing(1, 2, 2),
        "& > :not(:first-child)": {
          marginLeft: 0,
          marginBottom: theme.spacing(1),
        },
      },
    },
    actionsRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(1),
    },
    searchField: {
      width: "100%",
    },
    // Sticks the search field to the top of the viewport once the page
    // has scrolled it there, so it stays reachable while the member
    // list/table scrolls underneath - same pattern Catalog.jsx and
    // SongProposals.jsx use for their own sticky search rows. top
    // matches the real nav bar height per breakpoint (verified against
    // Page.module.scss's own comments: 64px desktop, 56px mobile), and
    // slides up to 0 in lockstep with the nav bar hiding itself on
    // scroll down (hideTopBar, passed in via props).
    stickySearch: {
      position: "sticky",
      zIndex: 1,
      backgroundColor: theme.palette.background.default,
      transition: theme.transitions.create("top"),
      top: ({ hideTopBar }) => (hideTopBar ? 0 : 56),
      [theme.breakpoints.up("md")]: {
        top: ({ hideTopBar }) => (hideTopBar ? 0 : 64),
      },
    },
    memberName: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      gap: theme.spacing(1),
    },
    leaderChip: {
      height: 20,
      fontSize: 11,
    },
    dialogAvatar: {
      width: 64,
      height: 64,
      fontSize: 24,
      margin: "0 auto",
    },
    tableHeaderCell: {
      backgroundColor: theme.palette.action.hover,
      fontWeight: 700,
      borderBottom: `2px solid ${theme.palette.divider}`,
    },
    tableRow: {
      cursor: "pointer",
    },
    tableNameCell: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
    },
    nameRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
    },
  };
});

// Search-only ordering (relevance, matching Catalog.jsx/SongProposals.jsx)
// while there's a query; otherwise you first (finding yourself in a long
// member list shouldn't need scrolling), then team leaders (same as
// WhatsApp pinning group admins to the top of the participant list),
// alphabetical within each group.
const visibleUsers = (users, search, currentUserId) => {
  if (search.trim()) {
    return filters.queryFilter(search, (u) => `${u.first_name} ${u.last_name} ${u.email}`)(users);
  }
  return [...users].sort((a, b) => {
    if (a.id === currentUserId || b.id === currentUserId) {
      return a.id === currentUserId ? -1 : 1;
    }
    if (a.is_team_leader !== b.is_team_leader) {
      return a.is_team_leader ? -1 : 1;
    }
    return `${a.first_name} ${a.last_name}`.trim().toLowerCase().localeCompare(
      `${b.first_name} ${b.last_name}`.trim().toLowerCase()
    );
  });
};

const lastActiveLabel = (user) =>
  user.last_login ? new Date(user.last_login).toLocaleDateString("en-GB") : "Never";

const MemberDialog = ({ member, currentUserId, canManage, onClose, onToggleLeader, onRemove }) => {
  const classes = useStyles();
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  // Removing is destructive enough to need a second tap - the dialog
  // swaps to an explanation + confirm instead of removing on first press.
  const [confirmingRemove, setConfirmingRemove] = React.useState(false);

  // Fresh state each time a different member is opened.
  React.useEffect(() => {
    setConfirmingRemove(false);
    setError("");
    setSaving(false);
  }, [member && member.id]);

  if (!member) return null;

  const isSelf = member.id === currentUserId;
  const name = `${member.first_name} ${member.last_name}`.trim() || member.email;

  const handleRemove = () => {
    setSaving(true);
    setError("");
    onRemove(member.id)
      .then(() => onClose())
      .catch((err) => {
        setSaving(false);
        setConfirmingRemove(false);
        setError(err.response?.data?.detail || "Couldn't remove them - try again.");
      });
  };

  const handleToggle = () => {
    setSaving(true);
    setError("");
    onToggleLeader(member.id, !member.is_team_leader)
      .then(() => onClose())
      .catch((err) => {
        setSaving(false);
        setError(err.response?.data?.detail || "Couldn't save that change - try again.");
      });
  };

  return (
    <Dialog open={!!member} onClose={onClose} fullWidth maxWidth="xs">
      <DialogContent>
        <Box pt={2} pb={1} textAlign="center">
          <Avatar className={classes.dialogAvatar} style={{ backgroundColor: avatarColor(member.id) }}>
            {initials(member)}
          </Avatar>
          <Box mt={1.5}>
            <Typography variant="h6">
              {`${member.first_name} ${member.last_name}`.trim()}
              {member.id === currentUserId && " (You)"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {member.email}
            </Typography>
          </Box>
          <Box mt={1}>
            <Typography variant="caption" color="textSecondary">
              {"Joined "}
              {new Date(member.date_joined).toLocaleDateString("en-GB")}
              {" · Last active "}
              {lastActiveLabel(member)}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <Box py={1.5}>
          <Typography variant="body2" color="textSecondary">
            Team leaders can create events, manage setlists, and approve song
            proposals.
          </Typography>
        </Box>
        {confirmingRemove && (
          <Typography variant="body2" paragraph>
            {`Remove ${name} from this team? They'll lose access to its songs and events, and get an email letting them know. Their account isn't deleted, and they can rejoin with a new invitation link.`}
          </Typography>
        )}
        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}
      </DialogContent>
      {confirmingRemove ? (
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setConfirmingRemove(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleRemove} color="secondary" disabled={saving}>
            Remove from team
          </Button>
        </DialogActions>
      ) : (
        <DialogActions className={classes.dialogActions}>
          <Button onClick={onClose} disabled={saving}>
            Close
          </Button>
          {canManage && !isSelf && (
            <Button onClick={() => setConfirmingRemove(true)} disabled={saving}>
              Remove from team
            </Button>
          )}
          {canManage && (
            <Button onClick={handleToggle} color="secondary" disabled={saving}>
              {member.is_team_leader ? "Remove as team leader" : "Make team leader"}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

// How far a member has got with the getting-started checklist (leaders and
// admins only: the server sends `setup` to nobody else). Hover for what's left.
export const SetupProgress = ({ setup }) =>
  setup ? (
    <Tooltip title={setup.missing && setup.missing.length ? `Not yet: ${setup.missing.join(", ")}` : "All set up"}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }} aria-label={`Set up ${setup.done} of ${setup.total}`}>
        <LinearProgress
          variant="determinate"
          color="secondary"
          value={(setup.done / setup.total) * 100}
          style={{ width: 64, height: 6, borderRadius: 3 }}
        />
        <Typography variant="caption" color="textSecondary">{`${setup.done}/${setup.total}`}</Typography>
      </span>
    </Tooltip>
  ) : null;

const membersTable = (users, classes, currentUserId, onSelect) => (
  <TableContainer>
    <Table size="medium" aria-label="members table">
      <TableHead>
        <TableRow>
          <TableCell className={classes.tableHeaderCell}>Name</TableCell>
          <TableCell className={classes.tableHeaderCell}>Role</TableCell>
          <TableCell className={classes.tableHeaderCell}>Last Active</TableCell>
          {users.some((u) => u.setup) && <TableCell className={classes.tableHeaderCell}>Set up</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {users.map((user) => (
          <TableRow key={user.id} hover className={classes.tableRow} onClick={() => onSelect(user)}>
            <TableCell component="th" scope="row">
              <span className={classes.tableNameCell}>
                <Avatar style={{ backgroundColor: avatarColor(user.id) }}>{initials(user)}</Avatar>
                <span>
                  {`${user.first_name} ${user.last_name}`.trim()}
                  {user.id === currentUserId && " (You)"}
                  <br />
                  <Typography variant="body2" color="textSecondary" component="span">
                    {user.email}
                  </Typography>
                </span>
              </span>
            </TableCell>
            <TableCell>
              {user.is_team_leader ? (
                <Chip label="Team leader" size="small" color="secondary" className={classes.leaderChip} />
              ) : user.is_affiliate ? (
                <Chip label="Coordinator" size="small" variant="outlined" className={classes.leaderChip} />
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Member
                </Typography>
              )}
            </TableCell>
            <TableCell>
              {user.last_login ? (
                lastActiveLabel(user)
              ) : (
                <Typography variant="body2" color="error">
                  Never
                </Typography>
              )}
            </TableCell>
            {users.some((u) => u.setup) && (
              <TableCell>
                <SetupProgress setup={user.setup} />
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const membersList = (users, classes, currentUserId, onSelect) => (
  <List>
    {users.map((user) => (
      <ListItem key={user.id} button divider onClick={() => onSelect(user)}>
        <ListItemAvatar>
          <Avatar style={{ backgroundColor: avatarColor(user.id) }}>{initials(user)}</Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={
            <span className={classes.memberName}>
              <span>
                {`${user.first_name} ${user.last_name}`.trim()}
                {user.id === currentUserId && " (You)"}
              </span>
              {user.is_team_leader && (
                <Chip label="Team leader" size="small" color="secondary" className={classes.leaderChip} />
              )}
              {!user.is_team_leader && user.is_affiliate && (
                <Chip label="Coordinator" size="small" variant="outlined" className={classes.leaderChip} />
              )}
            </span>
          }
          secondary={
            <>
              {user.email}
              <br />
              {"Last active: "}
              {user.last_login ? (
                lastActiveLabel(user)
              ) : (
                <Typography component="span" variant="body2" color="error">
                  Never
                </Typography>
              )}
              {user.setup && (
                <>
                  <br />
                  <Tooltip title={user.setup.missing && user.setup.missing.length ? `Not yet: ${user.setup.missing.join(", ")}` : "All set up"}>
                    <span>{`Set up: ${user.setup.done} of ${user.setup.total}`}</span>
                  </Tooltip>
                </>
              )}
            </>
          }
        />
      </ListItem>
    ))}
  </List>
);

export default ({
  team,
  currentUserId,
  canManage,
  fetchInvite,
  connectToSpotify,
  disconnectSpotify,
  updateMemberLeaderStatus,
  removeMember,
  renameTeam,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const largeScreen = useMediaQuery(theme.breakpoints.up("sm"));
  // Disconnecting Spotify takes a confirmation: it stops the team's playlist
  // syncing until an admin signs in to Spotify again, so one stray tap
  // shouldn't do it.
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = React.useState(false);
  const [disconnecting, setDisconnecting] = React.useState(false);
  const [disconnectError, setDisconnectError] = React.useState("");
  const closeDisconnectDialog = () => {
    if (disconnecting) return;
    setConfirmDisconnect(false);
    setDisconnectError("");
  };
  const handleConfirmDisconnect = () => {
    setDisconnecting(true);
    setDisconnectError("");
    Promise.resolve(disconnectSpotify())
      .then(() => setConfirmDisconnect(false))
      .catch(() => setDisconnectError("Couldn't disconnect Spotify - try again in a moment."))
      .finally(() => setDisconnecting(false));
  };
  // Same trigger (default threshold/hysteresis) Navigationbar's own
  // HideOnScroll uses, so the sticky search row below reads its
  // hidden/shown state without any actual coupling to it - see
  // Catalog.jsx/SongProposals.jsx for the identical pattern.
  const hideTopBar = useScrollTrigger();
  const classes = useStyles({ hideTopBar });
  const [search, setSearch] = React.useState("");
  const [selectedMember, setSelectedMember] = React.useState(null);
  const [editingName, setEditingName] = React.useState(false);
  const [nameDraft, setNameDraft] = React.useState("");
  const [nicknameDraft, setNicknameDraft] = React.useState("");
  const [nameSaving, setNameSaving] = React.useState(false);
  const [nameError, setNameError] = React.useState("");

  const startEditingName = () => {
    setNameDraft(team.name);
    setNicknameDraft(team.nickname || "");
    setNameError("");
    setEditingName(true);
  };

  const cancelEditingName = () => {
    setEditingName(false);
    setNameError("");
  };

  const saveName = () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      setNameError("Team name can't be empty.");
      return;
    }
    setNameSaving(true);
    if (nicknameDraft.trim().length > 50) {
      setNameError("Keep the nickname to 50 characters or fewer.");
      return;
    }
    renameTeam({ name: trimmed, nickname: nicknameDraft.trim() })
      .then(() => {
        setNameSaving(false);
        setEditingName(false);
      })
      .catch((err) => {
        setNameSaving(false);
        const data = err.response?.data;
        setNameError(data?.name?.[0] || data?.nickname?.[0] || data?.detail || "Couldn't save that change - try again.");
      });
  };

  const members = team ? visibleUsers(team.member, search, currentUserId) : [];
  // Re-derive from team.member (not the stale snapshot captured when the
  // dialog opened) so a save that changes the member's role is reflected
  // immediately if the dialog stays open, and so a re-render after close
  // never shows stale data.
  const selected = selectedMember
    ? team.member.find((m) => m.id === selectedMember.id) || null
    : null;

  return (
    <Box mt={2}>
      <Paper>
        <Box p={2}>
          {editingName ? (
            <Box>
              <TextField
                id="team-official-name"
                label="Official name"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                autoFocus
                fullWidth
                size="small"
                variant="outlined"
                error={!!nameError}
                disabled={nameSaving}
              />
              <Box mt={2}>
                <TextField
                  id="team-nickname"
                  label="Nickname (optional)"
                  value={nicknameDraft}
                  onChange={(e) => setNicknameDraft(e.target.value)}
                  fullWidth
                  size="small"
                  variant="outlined"
                  disabled={nameSaving}
                  inputProps={{ maxLength: 50 }}
                  helperText="What members call the team. It's used in invitations, emails and the team switcher; the official name stays on this page."
                />
              </Box>
              {nameError && (
                <Box mt={1}>
                  <Typography variant="body2" color="error">
                    {nameError}
                  </Typography>
                </Box>
              )}
              <Box mt={2} display="flex" style={{ gap: 8 }}>
                <Button variant="contained" color="secondary" size="small" onClick={saveName} disabled={nameSaving}>
                  Save
                </Button>
                <Button size="small" onClick={cancelEditingName} disabled={nameSaving}>
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <Box className={classes.nameRow}>
                <Typography
                  variant={largeScreen ? "h4" : "h5"}
                >{`${team.name}, ${team.city}`}</Typography>
                {canManage && (
                  <IconButton onClick={startEditingName} size="small" title="Rename team or set a nickname">
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              {team.nickname && teamLabel(team) !== team.name && (
                <Typography variant="body2" color="textSecondary">
                  {`Members know it as "${teamLabel(team)}"`}
                </Typography>
              )}
            </>
          )}
          <Box mt={largeScreen ? 3 : 2} className={classes.actionsRow}>
            {canManage && (
              <>
                <Button
                  onClick={() => setInviteOpen(true)}
                  variant="outlined"
                  size={largeScreen ? "medium" : "small"}
                  startIcon={<PersonAddIcon />}
                  title="Invite someone to the team"
                >
                  Invite a Friend
                </Button>
                <Button
                  component={Link}
                  to={{ pathname: "/setlist-templates", state: { from: location } }}
                  variant="outlined"
                  size={largeScreen ? "medium" : "small"}
                  startIcon={<LibraryMusicIcon />}
                >
                  Manage Setlist Templates
                </Button>
              </>
            )}
          </Box>
          <Box mt={2}>
            {team.spotify_username ? (
              <Box>
                <Box
                  className={classes.spotifyInfoBox}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  p={1}
                >
                  <Box display="flex" alignItems="center">
                    <SpotifyIcon fontSize="small" className={classes.spotifyInfoIcon} />
                    <Typography variant="body2">
                      Connected to Spotify as <strong>{team.spotify_username}</strong>
                    </Typography>
                  </Box>
                  <Button onClick={() => setConfirmDisconnect(true)} variant="outlined" size="small">
                    Disconnect
                  </Button>
                </Box>
                <Box mt={0.5}>
                  <Typography variant="caption" color="textSecondary">
                    Disconnecting from Spotify will not delete playlists or their
                    existing links stored in events.
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Button
                variant="outlined"
                size={largeScreen ? "medium" : "small"}
                startIcon={<SpotifyIcon />}
                onClick={connectToSpotify}
              >
                Connect to Spotify
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
      {canManage && (
        <InviteDialog
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          teamName={teamLabel(team)}
          fetchInvite={fetchInvite}
        />
      )}
      <Dialog open={confirmDisconnect} onClose={closeDisconnectDialog} fullWidth maxWidth="xs">
        <DialogTitle>Disconnect Spotify?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {"Your team won't be able to create or update Spotify playlists for events until a team admin connects Spotify again, which means signing in to Spotify once more. "}
            Playlists already on Spotify, and the links saved in events, are kept.
          </DialogContentText>
          {disconnectError && (
            <Typography variant="body2" color="error">
              {disconnectError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={closeDisconnectDialog} disabled={disconnecting} autoFocus>
            Cancel
          </Button>
          <Button onClick={handleConfirmDisconnect} color="secondary" disabled={disconnecting}>
            Disconnect Spotify
          </Button>
        </DialogActions>
      </Dialog>
      <Box mt={2} py={2}>
        <Box px={2} mb={1}>
          <Typography variant={largeScreen ? "h5" : "h6"}>
            {`Members · ${team.member.length}`}
          </Typography>
        </Box>
        <Box px={2} pt={1} pb={2} className={classes.stickySearch}>
          <TextField
            label="Search members"
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={classes.searchField}
          />
        </Box>
        <Paper>
          {largeScreen
            ? membersTable(members, classes, currentUserId, setSelectedMember)
            : membersList(members, classes, currentUserId, setSelectedMember)}
        </Paper>
      </Box>
      <MemberDialog
        member={selected}
        currentUserId={currentUserId}
        canManage={canManage}
        onClose={() => setSelectedMember(null)}
        onToggleLeader={updateMemberLeaderStatus}
        onRemove={removeMember}
      />
    </Box>
  );
};
